import { useEffect, useState } from 'react';

/**
 * `<input>` types that don't trigger an on-screen keyboard. Native pickers,
 * toggles, and file inputs all fall into this bucket — focusing one of
 * these doesn't push the BottomNav off-screen, so we treat them as if no
 * keyboard is up.
 */
const NON_KEYBOARD_INPUT_TYPES = new Set<string>([
	'button',
	'checkbox',
	'color',
	'file',
	'image',
	'radio',
	'range',
	'reset',
	'submit',
]);

function activeElementWantsKeyboard(): boolean {
	const ae = document.activeElement;
	if (ae == null || ae === document.body) return false;
	const tag = ae.tagName;
	if (tag === 'TEXTAREA') return true;
	if (tag === 'INPUT') {
		const t = (ae as HTMLInputElement).type?.toLowerCase() ?? 'text';
		return !NON_KEYBOARD_INPUT_TYPES.has(t);
	}
	if ((ae as HTMLElement).isContentEditable) return true;
	return false;
}

/**
 * Returns true when the user is focused inside an element that pulls up
 * the on-screen keyboard. Used to hide the BottomNav while the keyboard
 * is up — same pattern as Amazon, Slack, WhatsApp, Twitter native apps.
 *
 * Detection is via `focusin` / `focusout` (which bubble to document) on
 * an element whose tag/type implies a soft keyboard. This is more
 * reliable across browsers than visualViewport-height heuristics, which
 * lag the focus event on iOS and don't fire at all when a hardware
 * keyboard is attached on iPad / Capacitor-Android.
 */
export function useIsKeyboardLikelyOpen(): boolean {
	const [open, setOpen] = useState(false);

	useEffect(() => {
		const update = (): void => {
			setOpen(activeElementWantsKeyboard());
		};
		document.addEventListener('focusin', update);
		document.addEventListener('focusout', update);
		// Initial sync — if a deep-link landed with an autofocus, focusin
		// won't fire until the next focus change, so seed the value once.
		update();
		return () => {
			document.removeEventListener('focusin', update);
			document.removeEventListener('focusout', update);
		};
	}, []);

	return open;
}
